/* InitiativeDetails.js */
/* eslint-disable */

import React from "react";
import PropTypes from "prop-types";
import {
  TextField,
  Typography,
  Tooltip,
  Button,
  MenuItem,
  Switch,
} from "@material-ui/core";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import { ThreeDots } from "react-loader-spinner";

import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import ReactSelect from "react-select";
import { Section } from "components/Section/Section";
import { getAltLabel } from "utils/getAltLabel";

/* ─ helpers de exibição (só-leitura) ──────────────────────────────── */
import { displayFieldRenderers } from "components/FormHelper/displayFieldRenderersRead";

/* util para pegar label traduzida do estágio ------------------------ */
const getStageLabel = (stageObj, t, i18n) => {
  const { stage, label_pt, label_en } = stageObj;
  /* 1º tenta dicionário de tradução (caso exista) */
  const trans = t(`initiative.stage.${stage}`);
  if (trans !== `initiative.stage.${stage}`) return trans;
  /* 2º fallback para label salva no banco */
  return i18n.language.startsWith("pt") ? label_pt || label_en : label_en;
};

const getGridSizes = (type) => {
  const fullWidth = ["multichoices", "text"];
  return fullWidth.includes(type)
    ? { xs: 12, sm: 12, md: 12 }
    : { xs: 12, sm: 6, md: 4 };
};

/* util para truncar número para 1 casa decimal ---------------------- */
const truncateToOneDecimal = (num) => {
  if (num == null || isNaN(num)) return "–";
  const parsed = parseFloat(num);
  if (isNaN(parsed)) return "–";
  // Trunca para 1 casa decimal (não arredonda)
  return (Math.trunc(parsed * 10) / 10).toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

/* util p/ custom-fields em modo readonly ---------------------------- */
const renderSingleCustomFieldDetail = (
  meta,
  customFieldValues,
  classes,
  t,
  customStyles,
) => {
  const { field_name, field_label, label_key, field_type, choices = [] } = meta;

  const label = label_key ? t(`${label_key}`) : field_label;
  const value = customFieldValues?.[field_name];

  /* grid igual ao cadastro */
  const gridProps = getGridSizes(field_type);

  /* ---------- renderização por tipo ---------- */
  switch (field_type) {
    /* texto, data ---------------------------------------------- */
    case "text":
    case "char":
    case "date":
      return (
        <GridItem key={field_name} {...gridProps} style={{ marginBottom: 16}}>
          <TextField
            fullWidth
            label={label}
            value={value ?? "–"}
            disabled
            classes={{ root: classes.textFieldRoot }}
            InputProps={{
              readOnly: true,
              classes: { input: classes.inputText },
            }}
            InputLabelProps={{ classes: { root: classes.labelText } }}
          />
        </GridItem>
      );

    /* numérico - formatado com 1 casa decimal truncada --------- */
    case "number":
      return (
        <GridItem key={field_name} {...gridProps} style={{ marginBottom: 16}}>
          <TextField
            fullWidth
            label={label}
            value={truncateToOneDecimal(value)}
            disabled
            classes={{ root: classes.textFieldRoot }}
            InputProps={{
              readOnly: true,
              classes: { input: classes.inputText },
            }}
            InputLabelProps={{ classes: { root: classes.labelText } }}
          />
        </GridItem>
      );

    /* choice ---------------------------------------------------- */
    case "choice":
      return (
        <GridItem key={field_name} {...gridProps} style={{ marginBottom: 16}}>
          <TextField
            select
            fullWidth
            label={label}
            value={value ?? ""}
            disabled
            classes={{ root: classes.textFieldRoot }}
            InputProps={{
              readOnly: true,
              classes: { input: classes.inputText },
            }}
            InputLabelProps={{ classes: { root: classes.labelText } }}
          >
            {choices.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </TextField>
        </GridItem>
      );

    /* multichoices --------------------------------------------- */
    case "multichoices": {
      /* garante array */
      let current = [];
      if (Array.isArray(value)) current = value;
      else if (typeof value === "string" && value.trim() !== "")
        current = value.split(/[,;]+/).map((s) => s.trim());

      const options = current.map((v) => ({ value: v, label: v }));

      return (
        <GridItem key={field_name} {...gridProps} style={{ marginBottom: 16}}>
          <div className={classes.multiSelectContainer}>
            <label className={classes.labelMultiSelect}>{label}</label>
            <ReactSelect
              isMulti
              isDisabled
              value={options}
              styles={customStyles}
            />
          </div>
        </GridItem>
      );
    }

    /* boolean --------------------------------------------------- */
    case "boolean":
      return (
        <GridItem key={field_name} {...gridProps} style={{ marginBottom: 16}}>
          <div className={classes.switchContainer}>
            <Typography className={classes.labelSwitch}>{label}</Typography>
            <Switch checked={!!value} disabled />
          </div>
        </GridItem>
      );

    default:
      return null;
  }
};

/* ──────────────────────────────────────────────────────────────────── */
/*  C O M P O N E N T E                                               */
/* ──────────────────────────────────────────────────────────────────── */

const InitiativeDetails = ({
  /* context + estilo */
  t,
  classes,
  clientColor,
  i18n,
  customStyles,

  /* dados da iniciativa */
  selectedInitiative,
  userDetails,

  /* reused helpers */
  renderCustomFieldsDetail, 
  renderKpis, 
  onDownloadFile, 

  /* visibilidade */
  showIdentification = true,
  showDates = true,
  showDescription = true,
  showCustomFields = true,
  showKpis = true,
  showClassification = true,
  showEditors = true,
  showAttachments = true,
}) => {
  if (!selectedInitiative) return null;


  /* -------------------------------------------------------------- */
  /*                 ⬇ download handler para anexos                */
  /* -------------------------------------------------------------- */
  const [loadingDownload, setLoadingDownload] = React.useState({});

  const handleDownload = async (blob, name) => {
    if (loadingDownload[blob]) return;
    setLoadingDownload((p) => ({ ...p, [blob]: true }));
    try {
      await onDownloadFile(blob, name);
    } finally {
      setLoadingDownload((p) => ({ ...p, [blob]: false }));
    }
  };

  /* -------------------------------------------------------------- */
  /*         ░░░░░░░░ 1. SESSÕES “DYNAMIC” ░░░░░░░░                */
  /* -------------------------------------------------------------- */

  const hasDynamic =
    Array.isArray(userDetails.stage_layout) &&
    userDetails.stage_layout.length > 0;

  const renderDynamicSections = () =>
    userDetails.stage_layout.map((stageObj) => {
      const title = getStageLabel(stageObj, t, i18n);

      return (
        <Section key={stageObj.stage} title={title} clientColor={clientColor}>
          {stageObj.fields.map((fid) => {
            /* 1️⃣ custom field ---------------------------------------- */
            if (fid.startsWith("custom:")) {
              const fname = fid.split(":")[1];
              const meta = selectedInitiative.customFieldsMetadata?.find(
                (c) => c.field_name === fname,
              );
              return meta
                ? renderSingleCustomFieldDetail(
                    meta,
                    selectedInitiative.customFields,
                    classes,
                    t,
                    customStyles,
                  )
                : null;
            }

            /* 2️⃣ fixed basic fields ---------------------------------- */
            const Renderer = displayFieldRenderers[fid];
            return Renderer ? (
              <Renderer
                key={fid}
                data={selectedInitiative}
                classes={classes}
                t={t}
                customStyles={customStyles}
                userDetails={userDetails}
              />
            ) : null;
          })}
        </Section>
      );
    });

  /* -------------------------------------------------------------- */
  /*         ░░░░░░░░ 2. LEGACY SESSÕES (fallback) ░░░░░░░░        */
  /* -------------------------------------------------------------- */

  const renderLegacySections = () => (
    <>
      {/* IDENTIFICATION ------------------------------------------ */}
      {showIdentification && (
        <Section
          title={t("initiative_selected.section_identification")}
          clientColor={clientColor}
        >
          <GridItem xs={12} sm={2} style={{ marginBottom: 20 }}>
            <TextField
              disabled
              fullWidth
              label={t("new_initiatives.fields.initiative_id")}
              value={selectedInitiative.id_internal || ""}
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                readOnly: true,
                classes: { input: classes.inputText },
              }}
              InputLabelProps={{ classes: { root: classes.labelText } }}
            />
          </GridItem>

          <GridItem xs={12} sm={10} style={{ marginBottom: 20 }}>
            <TextField
              disabled
              fullWidth
              label={t("new_initiatives.fields.initiative_name")}
              value={selectedInitiative.initiative}
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                readOnly: true,
                classes: { input: classes.inputText },
              }}
              InputLabelProps={{ classes: { root: classes.labelText } }}
            />
          </GridItem>

          <GridItem xs={12} sm={6} style={{ marginBottom: 14 }}>
            <TextField
              disabled
              fullWidth
              label={getAltLabel("new_initiatives.fields.branch", userDetails, t)}
              value={selectedInitiative.branch || ""}
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                readOnly: true,
                classes: { input: classes.inputText },
              }}
              InputLabelProps={{ classes: { root: classes.labelText } }}
            />
          </GridItem>

          <GridItem xs={12} sm={6} style={{ marginBottom: 14 }}>
            <TextField
              disabled
              fullWidth
              label={getAltLabel("new_initiatives.fields.workfront", userDetails, t)}
              value={selectedInitiative.workfront || ""}
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                readOnly: true,
                classes: { input: classes.inputText },
              }}
              InputLabelProps={{ classes: { root: classes.labelText } }}
            />
          </GridItem>

          <GridItem xs={12} sm={4}>
            <TextField
              disabled
              fullWidth
              label={t("new_initiatives.fields.initiative_leader")}
              value={selectedInitiative.leader || ""}
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                readOnly: true,
                classes: { input: classes.inputText },
              }}
              InputLabelProps={{ classes: { root: classes.labelText } }}
            />
          </GridItem>

          <GridItem xs={12} sm={4}>
            <TextField
              disabled
              fullWidth
              label={t("new_initiatives.fields.initiative_leader_2")}
              value={selectedInitiative.leader2 || ""}
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                readOnly: true,
                classes: { input: classes.inputText },
              }}
              InputLabelProps={{ classes: { root: classes.labelText } }}
            />
          </GridItem>

          <GridItem xs={12} sm={4}>
            <TextField
              disabled
              fullWidth
              label={t("new_initiatives.fields.initiative_pmo")}
              value={selectedInitiative.pmo || ""}
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                readOnly: true,
                classes: { input: classes.inputText },
              }}
              InputLabelProps={{ classes: { root: classes.labelText } }}
            />
          </GridItem>
        </Section>
      )}

      {/* DATES ---------------------------------------------------- */}
      {showDates && (
        <Section
          title={t("initiative_selected.section_dates")}
          clientColor={clientColor}
        >
          {[
            {
              label: t("new_initiatives.fields.imp_start_date"),
              value: selectedInitiative.imp_start_date,
            },
            {
              label: t("new_initiatives.fields.imp_end_date"),
              value: selectedInitiative.imp_end_date,
            },
            {
              label: t("new_initiatives.fields.results_start_date"),
              value: selectedInitiative.results_start_date,
            },
            {
              label: t("new_initiatives.fields.results_end_date"),
              value: selectedInitiative.results_end_date,
            },
          ].map((d, i) => (
            <GridItem xs={6} sm={3} key={i}>
              <TextField
                disabled
                fullWidth
                type="date"
                label={d.label}
                value={d.value || ""}
                InputProps={{ readOnly: true, style: { color: "#3C4858" } }}
                InputLabelProps={{
                  shrink: true,
                  classes: { root: classes.labelText },
                }}
                classes={{ root: classes.textFieldRoot }}
              />
            </GridItem>
          ))}
        </Section>
      )}

      {/* DESCRIPTION / RISKS -------------------------------------- */}
      {showDescription && (
        <Section
          title={t("initiative_selected.section_description")}
          clientColor={clientColor}
        >
          <GridItem xs={12}>
            <TextField
              disabled
              fullWidth
              multiline
              rows={4}
              label={t("new_initiatives.fields.description")}
              value={selectedInitiative.description || ""}
              classes={{ root: classes.textFieldRoot }}
              InputProps={{
                readOnly: true,
                classes: { input: classes.inputText },
              }}
              InputLabelProps={{
                classes: { root: classes.labelText },
                shrink: true,
              }}
            />
          </GridItem>

          {userDetails.has_forms_risks && (
            <GridItem xs={12}>
              <TextField
                disabled
                fullWidth
                multiline
                rows={4}
                label={t("new_initiatives.fields.risks")}
                value={selectedInitiative.risks || ""}
                classes={{ root: classes.textFieldRoot }}
                InputProps={{
                  readOnly: true,
                  classes: { input: classes.inputText },
                }}
                InputLabelProps={{
                  classes: { root: classes.labelText },
                  shrink: true,
                }}
              />
            </GridItem>
          )}
        </Section>
      )}

      {/* CUSTOM FIELDS (legacy) ----------------------------------- */}
      {showCustomFields &&
        selectedInitiative.customFieldsMetadata &&
        selectedInitiative.customFieldsMetadata.length > 0 &&   
        selectedInitiative.customFields &&
        Object.keys(selectedInitiative.customFields || {}).length > 0 && (
          <Section
            title={t("initiative_selected.section_custom_fields")}
            clientColor={clientColor}
          >
            {(selectedInitiative.customFieldsMetadata || []).map((meta) =>
              renderSingleCustomFieldDetail(
                meta,
                selectedInitiative.customFields,
                classes,
                t,
                customStyles,
              ),
            )}
          </Section>
        )}
    </>
  );

  const renderClassificationSection = () =>
    !hasDynamic &&
    showClassification && (
      <Section
        title={t("initiative_selected.section_classification")}
        clientColor={clientColor}
      >
        {userDetails.has_forms_impact && (
          <GridItem xs={12} md={4}>
            <label className={classes.labelMultiSelect}>
              {t("new_initiatives.fields.initiative_impact")}
            </label>
            <ReactSelect
              isMulti
              isDisabled
              value={(selectedInitiative.initiativeImpacts || []).map((i) => ({
                label: i,
                value: i,
              }))}
              styles={customStyles}
            />
          </GridItem>
        )}

        <GridItem xs={12} md={4}>
          <label className={classes.labelMultiSelect}>
            {getAltLabel("new_initiatives.fields.tags", userDetails, t)}
          </label>
          <ReactSelect
            isMulti
            isDisabled
            value={(selectedInitiative.tags || []).map((tag) => ({
              label: tag,
              value: tag,
            }))}
            styles={customStyles}
          />
        </GridItem>

        <GridItem xs={12} md={4}>
          <label className={classes.labelMultiSelect}>
            {t("new_initiatives.fields.access_group")}
          </label>
          <ReactSelect
            isMulti
            isDisabled
            value={(selectedInitiative.accessGroups || []).map((g) => ({
              label: g,
              value: g,
            }))}
            styles={customStyles}
          />
        </GridItem>
      </Section>
    );

  const renderEditorsSection = () =>
    !hasDynamic &&
    showEditors &&
    userDetails.has_forms_editors &&
    selectedInitiative.editors && (
      <Section
        title={t("initiative_selected.section_editors")}
        clientColor={clientColor}
      >
        {["one_page", "implementation", "results"].map((k) => (
          <GridItem xs={12} md={4} key={k}>
            <label className={classes.labelMultiSelect}>
              {t(
                `new_initiatives.fields.editors_${k === "one_page" ? "one_page" : k}`,
              )}
            </label>
            <ReactSelect
              isMulti
              isDisabled
              value={(selectedInitiative.editors[k] || []).map((mail) => ({
                label: mail,
                value: mail,
              }))}
              styles={customStyles}
            />
          </GridItem>
        ))}
      </Section>
    );

  /* -------------------------------------------------------------- */
  /*                 ⬇  ATTACHMENTS                                 */
  /* -------------------------------------------------------------- */

  const { attachments } = selectedInitiative;
  const bc = attachments?.business_case;
  const imp = attachments?.implementation_plan;
  const extra = attachments?.extra_files || [];

  const AttachmentRow = ({ label, att }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        background: "#f9f9f9",
        padding: "8px 12px",
        borderRadius: 6,
        border: `1px solid ${clientColor}20`,
        marginBottom: 6,
        marginLeft: 10,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          style={{
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={att.file_name}
        >
          {label || att.file_name}
        </Typography>
        {label && (
          <Typography variant="caption" color="textSecondary">
            {att.file_name}
          </Typography>
        )}
      </div>
      <Tooltip title={t("initiative_selected.download")}>
        <Button
          color="client"
          className={classes.cardButton}
          disabled={loadingDownload[att.blob_name]}
          onClick={() => handleDownload(att.blob_name, att.file_name)}
          style={{ transform: "translateY(-6px)" }}
        >
          {loadingDownload[att.blob_name] ? (
            <ThreeDots color="white" height={18} width={18} />
          ) : (
            <CloudDownloadIcon className={classes.cardButtonIcon} />
          )}
        </Button>
      </Tooltip>
    </div>
  );

  /* -------------------------------------------------------------- */
  /*                       R E N D E R                              */
  /* -------------------------------------------------------------- */
  return (
    <GridContainer spacing={2} style={{ marginTop: 8 }}>
      {hasDynamic ? renderDynamicSections() : renderLegacySections()}

      {renderClassificationSection()}
      {renderEditorsSection()}

      {/* KPIs (mesma lógica de antes) */}
      {showKpis && (
        <Section
          title={t("initiative_selected.section_kpis")}
          clientColor={clientColor}
        >
          {renderKpis()}
        </Section>
      )}

      {/* ATTACHMENTS – permanece igual ---------------------------- */}
      {showAttachments && (
        <Section
          title={t("initiative_selected.section_attachments")}
          clientColor={clientColor}
        >
          <GridContainer spacing={2}>
            {/* vazio */}
            {!bc && !imp && extra.length === 0 && (
              <GridItem xs={12} style={{ marginLeft: 10 }}>
                <Typography variant="body2" color="textSecondary">
                  {t("initiative_selected.no_attachments")}
                </Typography>
              </GridItem>
            )}

            {userDetails.has_business_case && bc && (
              <GridItem xs={12} sm={6}>
                <AttachmentRow
                  label={t("initiative_selected.business_case")}
                  att={bc}
                />
              </GridItem>
            )}

            {imp && (
              <GridItem xs={12} sm={6}>
                <AttachmentRow
                  label={t("initiative_selected.imp_plan")}
                  att={imp}
                />
              </GridItem>
            )}

            {(extra || []).length > 0 && (
              <>
                <GridItem xs={12} style={{ marginLeft: 10, marginTop: 6 }}>
                  <Typography variant="subtitle2">
                    {t("initiative_selected.extra_files")}
                  </Typography>
                </GridItem>
                {(extra || []).map((e, idx) => (
                  <GridItem xs={12} sm={6} md={4} key={idx}>
                    <AttachmentRow att={e} />
                  </GridItem>
                ))}
              </>
            )}
          </GridContainer>
        </Section>
      )}
    </GridContainer>
  );
};

/* ───────────────────────── propTypes (sem alterações) ────────────── */
InitiativeDetails.propTypes = {
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  clientColor: PropTypes.string.isRequired,
  customStyles: PropTypes.object.isRequired,

  selectedInitiative: PropTypes.object,
  userDetails: PropTypes.object.isRequired,

  renderCustomFieldsDetail: PropTypes.func.isRequired,
  renderKpis: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,

  /* toggles */
  showIdentification: PropTypes.bool,
  showDates: PropTypes.bool,
  showDescription: PropTypes.bool,
  showCustomFields: PropTypes.bool,
  showKpis: PropTypes.bool,
  showClassification: PropTypes.bool,
  showEditors: PropTypes.bool,
  showAttachments: PropTypes.bool,
};

export default InitiativeDetails;
